/* eslint-disable no-case-declarations */

import classnames from "classnames";
import { LocationDescriptorObject } from "history";
import React from "react";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { Link } from "react-router";
import { push, replace } from "react-router-redux";
import Sticky from "react-stickynode";
import { Action } from "redux";

import * as WebRoutes from "@travellocal/utils/webRoutes"
import * as DateUtil from "@travellocal/utils/dateHelpers";
import { FlowBox, Loader, SvgIcon, HtmlRenderBlock } from "../../../core/atoms/atoms";
import { CardContainer } from "../../../shared/components/inspiration/cardContainer";
import { GetInspiredCard } from "../../../shared/components/inspiration/cards";
import { getTypeIcon } from "../../../shared/components/inspiration/resources";
import { apiPromiseHook, createEnterHookPromise } from "../../../shared/flux/actions/enterHooks";
import { CollectionKeyHelper } from "../../../shared/helpers/contentCollectionHelpers";
import { ContentTypeEnumHelper } from "@travellocal/utils/enumHelper";
import { LocationCard, HeroSection, GoogleAnalyticsHelper } from "@travellocal/ui";
import { buildMetaTags, buildStructuredDataSnippet } from "../../../shared/helpers/metaData";
import { MatchState } from "../../../shared/helpers/reduxHelpers";
import { MatchStateStatus } from "../../../shared/helpers/reduxHelpers";
import ResponsiveContainer from "../../../shared/helpers/responsiveContainer";
import { SocialShareIcons } from "../../components/controls/socialShare";
import { InspirationDisqusComments } from "../../components/disqusComments";
import { HubspotNewsletterSignupForm } from "../../components/hubspotForm/hubspotForm";
import { SwipeCarousel } from "../../components/swipeCarousel/swipeCarousel";
import { ActionPromises } from "../../state/actionPromises";

import "./contentSingle.scss";

interface StateProps {
  router: RouterReducer;
  content: IState<IContent>;
}

interface DispatchProps {
  dispatch: (a: Action) => DispatchedPromise<unknown>;
  navigate: (route: string) => void;
  replaceUrl: (route: string) => void;
}

type Props = StateProps & DispatchProps;

const stateToProps = (state: IWebStoreState): StateProps => ({
  router: state.routing,
  content: state.contentItem,
});

const dispatchToProps = (dispatch): DispatchProps => ({
  dispatch,
  navigate: (route: string) => dispatch(push(route)),
  replaceUrl: (route: string) => dispatch(replace(route)),
});

/**
 * Container for all bits of content, handles different content types, normalising URL, gated/ non gated etc
 */
class LandingComponent extends React.Component<Props, unknown> {

  private chooseContentComponent = () => {
    const content = this.props.content.data;
    switch (content.type) {
      case ContentType.BlogPost:
        return (
          <HtmlRenderBlock
            tag="article"
            className="blog-post"
            htmlString={content?.content?.html}
          />
        );
      default:
        // This will probably never be shown, since react-router should throw a 404 instead.
        const returnFilterSet = {
          themes: content.themes ? content.themes.map(t => t.id) : [],
          locations: content.locations ? content.locations.map(l => l.id) : [],
        } as IListContentParams;

        return (
          <div>
            <h3>Something went wrong</h3>
            <p>Couldn&apos;t load content at {WebRoutes.contentDetail(content)}.</p>
            <p><Link to={WebRoutes.inspiration(returnFilterSet)}>Need some inspiration?</Link></p>
          </div>
        );
    }
  }

  private getRelatedFilterSet = (content: IContent): IListContentParams => {
    if (!content) {
      return null;
    }

    const themes = content.themes ? content.themes.map(x => x.id) : [];
    const locations = content.locations ? content.locations.map(x => x.id) : [];
    return { themes, locations };
  }

  private buildMetaTags = (content: IContent) => {
    const extraTags = [
      {
        property: "og:article:published_time",
        content: content.publishedTimestamp ? content.publishedTimestamp.value : null,
      },
    ];

    // Add more content types here
    switch (content.type) {
      case ContentType.BlogPost:
        extraTags.push({ property: "og:type", content: "article" });
        break;
    }
    return buildMetaTags(
      content.meta,
      `${globalConfig.wwwBaseUrl}${WebRoutes.contentDetail(content, true)}`,
      content.heroImage,
      { title: content.title },
      extraTags,
    );
  }

  private contentStructuredData = (content: IContent) => {
    const commonData = {
      image: content.heroImage ? content.heroImage.smallUrl : "",
      mainEntityofPage: `${globalConfig.wwwBaseUrl}${WebRoutes.contentDetail(content, true)}`,
      datePublished: content.publishedTimestamp ? content.publishedTimestamp.value : null,
      dateCreated: content.createdTimestamp ? content.createdTimestamp.value : null,
      dateModified: content.updatedTimestamp ? content.updatedTimestamp.value : null,
      description: content.meta ? content.meta.descriptionTag : null,
    };

    // Add more content types here
    switch (content.type) {
      case ContentType.BlogPost:
        const blogPostData = {
          headline: content.title,
          publisher: {
            "@type": "Organization",
            "name": "TravelLocal",
            "logo": {
              type: "ImageObject",
              url: "https://www.travellocal.com/images/logo-green.png",
            },
          },
          author: {
            "@type": "Person",
            "name": content.guestAuthor || `${content.author.firstName} ${content.author.lastName}`,
          },
        };
        const structuredData = { ...commonData, ...blogPostData };
        return buildStructuredDataSnippet("BlogPosting", structuredData);
      default:
        return buildStructuredDataSnippet("Article", commonData);
    }
  }

  private renderHelmet = () => {
    return MatchStateStatus(this.props.content.status, {
      pending: () => <Helmet title="Loading... | TravelLocal" />,
      ok: () => {
        const content = this.props.content.data;

        return (
          <Helmet title={content.meta.titleTag || content.title}
            meta={this.buildMetaTags(content)}
            script={[
              this.contentStructuredData(content),
            ]}
            link={[
              { rel: "canonical", href: `${globalConfig.wwwBaseUrl}${WebRoutes.contentDetail(content, true)}` },
            ]} />
        );
      },
      error: () => <Helmet title="Error | TravelLocal" />,
    });
  }

  public componentDidMount() {
    // It's possible to navigate to /inspiration/:type/:id - i.e. w/o :slug
    // Normalise the URL according to the content obj.
    // Also ensures old slugs are corrected after they get changed.
    const route = this.props.router.locationBeforeTransitions as LocationDescriptorObject;
    const correctPath = WebRoutes.contentDetail(this.props.content.data);
    if (route.pathname !== correctPath) {
      this.props.replaceUrl(correctPath);
    }
  }

  public render() {
    const contentState = this.props.content;

    return MatchState(contentState, {
      pending: () => <Loader />,
      error: () => (
        <div className="width-wrapper p-top-large">
          <h1>Sorry! Something went wrong and we can&apos;t show you this page.</h1>
          <p><Link to={WebRoutes.inspiration()}>Click here to see all of our inspiring articles</Link></p>
        </div>
      ),
      ok: () => this.renderContent(contentState.data),
    }) as JSX.Element;
  }

  private renderContent(content: IContent) {
    const heroImage = content ? content.heroImage : null;
    const contentTypeLabel = content ? ContentTypeEnumHelper.toFriendlyName(content.type) : "Item";
    const relatedFilterSet = this.getRelatedFilterSet(content);

    const className = classnames("content-single non-gated", {
      [content ? content.type : ""]: content != null,
    });

    const longMessage = (content?.meta ? content.meta.descriptionTag || content.meta.titleTag : null) || content?.title;

    return (
      <div className={className}>
        {this.renderHelmet()}

        <HeroSection heroImage={heroImage}>
          {MatchStateStatus(this.props.content.status, {
            pending: () => (
              <h1>Loading...</h1>
            ),
            ok: () => (
              <div className="inner">
                <SvgIcon className={classnames("content-type-icon", "text--caps")} svg={getTypeIcon(content.type)}>{contentTypeLabel}</SvgIcon>
                <h1>{content.title}</h1>
                <p><em>by {content.author ? `${content.author.firstName} ${content.author.lastName}` : content.guestAuthor}</em></p>
                <p><em>published <time>{DateUtil.formatDateTime(content.publishedTimestamp, "Do MMM YYYY h:mma")}</time></em></p>
              </div>
            ),
            error: () => (
              <h1>😓</h1>
            ),
          })}
        </HeroSection>

        <FlowBox className="body width-wrapper marketing-2cols-sidebar">
          <div className="content">
            {MatchStateStatus(this.props.content.status, {
              pending: () => <Loader />,
              ok: this.chooseContentComponent,
              error: () => <p>&quot;Something went wrong!&quot;</p>,
            })}
          </div>
          <Sticky className="secondary" innerZ={4} top=".site-header" bottomBoundary=".body">
            <aside>
              <div className="newsletter-trail">
                <h4>Subscribe to our newsletter!</h4>
                <p>We&apos;ll send you tips, tricks, and inspiration for your travels, and keep you updated with insights from our local experts.</p>
                <HubspotNewsletterSignupForm />
              </div>

              {MatchStateStatus(this.props.content.status, {
                pending: () => <Loader />,
                ok: () => {
                  const countries = content.locations.filter(
                    loc => loc.locationType === LocationType.Country && loc.locationStatus === LocationStatus.Live
                  );
                  if (countries.length < 1) {
                    return null;
                  }

                  return (
                    <div className="featured-destinations">
                      <h4>Featured destinations</h4>
                      <FlowBox>
                        {countries.map(location =>
                          <LocationCard
                            location={location}
                            fallbackUrl={content.heroImage ? content.heroImage.smallUrl : null}
                            onClick={() => GoogleAnalyticsHelper.logEvent("Select Country", "Click", "Blog post card")}
                            key={Math.random()}
                          />,
                        )}
                      </FlowBox>
                    </div>
                  );
                },
                error: () => <div />,
              })}

              <div className="share">
                <h4>Share this {contentTypeLabel.toLowerCase()}</h4>

                {MatchStateStatus(this.props.content.status, {
                  pending: () => <Loader />,
                  ok: () => (
                    <SocialShareIcons
                      imageUrl={heroImage ? heroImage.largeUrl : "/images/missing-image.jpg"}
                      link={`${globalConfig.wwwBaseUrl}${WebRoutes.contentDetail(content)}`}
                      shortMessage={content.title}
                      longMessage={longMessage}
                    />
                  ),
                  error: () => <div />,
                })}
              </div>
            </aside>
          </Sticky>
        </FlowBox>

        <div className="related-content">
          <div className="message-wrap">
            <p>Here&apos;s some more holiday inspiration for you...</p>
          </div>

          <ResponsiveContainer
            mobile={() => (
              <CardContainer
                collectionKey={CollectionKeyHelper.ContentDetailRelated(content.id)}
                cardLimit={3}
                filters={relatedFilterSet}
                seedItem={content}
                renderCardWrapper={elements => (
                  <SwipeCarousel>
                    {elements}
                    <GetInspiredCard filterSet={relatedFilterSet} />
                  </SwipeCarousel>
                )}
              />
            )}
            tablet={() => (
              <CardContainer
                collectionKey={CollectionKeyHelper.ContentDetailRelated(content.id)}
                cardLimit={3}
                filters={relatedFilterSet}
                seedItem={content}
                columnClassName="column is-6-tablet is-4-desktop is-3-widescreen is-3-fullhd"
              >
                <GetInspiredCard filterSet={relatedFilterSet} />
              </CardContainer>
            )}
          />
        </div>

        <a id="comments" />
        {MatchStateStatus(this.props.content.status, {
          pending: () => <Loader />,
          ok: () => (
            <InspirationDisqusComments className="width-wrapper" content={content}>
              <h2>Comments on <em>{content.title}</em></h2>
              <p>What did you think about this {contentTypeLabel.toLocaleLowerCase()}? Let us know in the comments!</p>
              <hr />
            </InspirationDisqusComments>
          ),
        })}
      </div>
    );
  }
}

export const SingleItemLanding = connect(stateToProps, dispatchToProps)(LandingComponent);

export const inspirationSingleOnEnter = apiPromiseHook<unknown>(
  createEnterHookPromise((router, state: IWebStoreState) => {
    const id = router.params.id;
    // URL normalisation from /inspiration/:type/:id to /inspiration/:type/:id/:slug runs this hook twice
    // No need.
    if (!state.contentItem || !state.contentItem.data || String(state.contentItem.data.id) !== id) {
      return ActionPromises.getContentAndRelatedById(id, 5);
    }
  }),
);
